"use client";

import {
  Badge,
  Button,
  cn,
  confirm,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Loader,
  Modal,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  Banknote,
  History,
  Landmark,
  Package,
  Printer,
  RefreshCw,
  ShieldCheck,
  Wallet,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  applyExclusivePosTax,
  effectivePosTaxPercentDisplay,
  formatPosTaxPercentLabel,
  roundMoneyPos,
} from "@/modules/merchant/domain/pos-exclusive-tax";
import {
  canUseMerchantPermission,
  type PosReturnSummary,
  type PosSaleDetailResponse,
} from "@/modules/merchant/presentation/api/client";
import { formatPosBranchDisplayLabel } from "@/modules/merchant/presentation/lib/merchant-pos-branch-label";
import { getStoredMerchantPosBranchId } from "@/modules/merchant/presentation/lib/merchant-pos-branch-storage";
import { printPosSaleReceipt } from "@/modules/merchant/presentation/lib/print-pos-sale-receipt";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { PosSaleActivitySection } from "@/modules/merchant/presentation/ui/PosSaleActivitySection";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { ShopPosSettlePartialModal } from "@/modules/merchant/presentation/ui/ShopPosSettlePartialModal";
import {
  saleStatusBadgeVariant,
  statusLabel,
} from "@/modules/merchant/presentation/ui/shop-pos/pos-sale-status";
import { ShopPosReturnModal } from "@/modules/merchant/presentation/ui/shop-pos/ShopPosReturnModal";
import { ShopPosSalesHistoryFilters } from "@/modules/merchant/presentation/ui/shop-pos/ShopPosSalesHistoryFilters";
import { ShopPosSalesHistoryList } from "@/modules/merchant/presentation/ui/shop-pos/ShopPosSalesHistoryList";
import { ShopPosSalesHistoryToolbar } from "@/modules/merchant/presentation/ui/shop-pos/ShopPosSalesHistoryToolbar";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { formatPrice } from "@/shared/lib/currency";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  usePosAbandonHoldMutation,
  usePosConfirmHoldMutation,
  usePosRefundSaleMutation,
  usePosReturnSummariesQuery,
  usePosSaleDetailQuery,
  usePosSalesListQuery,
  usePosVoidSaleMutation,
} from "../api/queries";

const ROUTE = "/app/shops/$shopId/pos/sales" as const;
const FILTER_ALL = "__all__";

type SalesSearch = {
  offset?: number;
  limit?: number;
  status?: string;
  createdFrom?: string;
  createdTo?: string;
  saleNumber?: string;
  customerUserId?: string;
  customerSearch?: string;
};

function pickSearchString(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() !== "" ? v.trim() : undefined;
}

/** ຄ່າ search ຄົບຊຸດສຳລັບ TanStack Router (ທຸກຟິວດຕ້ອງມີ) */
type PosSalesRouteSearch = {
  offset: number;
  limit: number;
  status: string | undefined;
  createdFrom: string | undefined;
  createdTo: string | undefined;
  saleNumber: string | undefined;
  customerUserId: string | undefined;
  customerSearch: string | undefined;
};

function posSalesNavSearch(
  raw: SalesSearch,
  o: { limit: number; offset: number },
): PosSalesRouteSearch {
  return {
    offset: o.offset,
    limit: o.limit,
    status:
      typeof raw.status === "string" && raw.status.trim() !== ""
        ? raw.status.trim()
        : undefined,
    createdFrom: pickSearchString(raw.createdFrom),
    createdTo: pickSearchString(raw.createdTo),
    saleNumber: pickSearchString(raw.saleNumber),
    customerUserId: pickSearchString(raw.customerUserId),
    customerSearch: pickSearchString(raw.customerSearch),
  };
}

function numFromSearch(v: unknown, fallback: number): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v !== "") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function paymentMethodLabel(method: string): string {
  switch (method) {
    case "cash":
      return "ເງິນສົດ";
    case "transfer":
      return "ໂອນ";
    case "wallet":
      return "Wallet";
    default:
      return method;
  }
}

function sumPaymentsPaid(detail: PosSaleDetailResponse): number {
  return round2(
    detail.payments.reduce((s, p) => {
      const inv =
        p.invoiceEquivalentAmount != null &&
        Number.isFinite(p.invoiceEquivalentAmount)
          ? p.invoiceEquivalentAmount
          : Number.isFinite(p.amount)
            ? p.amount
            : 0;
      return s + inv;
    }, 0),
  );
}

const paymentMethodIconClass =
  "size-4 shrink-0 text-[hsl(210_90%_48%)]" as const;

function PaymentMethodIcon({ method }: { method: string }) {
  switch (method) {
    case "cash":
      return <Banknote className={paymentMethodIconClass} aria-hidden />;
    case "transfer":
      return <Landmark className={paymentMethodIconClass} aria-hidden />;
    case "wallet":
      return <Wallet className={paymentMethodIconClass} aria-hidden />;
    default:
      return <Banknote className={paymentMethodIconClass} aria-hidden />;
  }
}

export function ShopPosSalesHistoryPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate({ from: ROUTE });
  const search = (useSearch({ from: ROUTE }) ?? {}) as SalesSearch;

  const limit = Math.min(
    100,
    Math.max(1, Math.floor(numFromSearch(search?.limit, 20))),
  );
  const offset = Math.max(0, Math.floor(numFromSearch(search?.offset, 0)));

  const rawStatus =
    typeof search.status === "string" ? search.status.trim() : "";
  const statusKey =
    rawStatus === "draft" ||
    rawStatus === "paid" ||
    rawStatus === "voided" ||
    rawStatus === "refunded" ||
    rawStatus === "partial" ||
    rawStatus === "returned" ||
    rawStatus === "exchanged"
      ? rawStatus
      : FILTER_ALL;

  const createdFrom = pickSearchString(search.createdFrom);
  const createdTo = pickSearchString(search.createdTo);
  const saleNumberQ = pickSearchString(search.saleNumber);
  const customerSearchQ = pickSearchString(search.customerSearch);

  const { currentShop, currentPermissions } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel;
  const listBranchId =
    (shopId && getStoredMerchantPosBranchId(shopId)) ||
    currentShop?.branchId ||
    undefined;
  const branchId = listBranchId;

  // ຄືນ/ປ່ຽນ = ກອງຕາມ pos_returns.type (ບໍ່ແມ່ນ posSales.status)
  const isReturnFilter = statusKey === "returned" || statusKey === "exchanged";
  const listQuery = {
    branchId: listBranchId,
    status:
      statusKey === FILTER_ALL || isReturnFilter
        ? undefined
        : (statusKey as "draft" | "paid" | "voided" | "refunded" | "partial"),
    returnType:
      statusKey === "returned"
        ? ("return" as const)
        : statusKey === "exchanged"
          ? ("exchange" as const)
          : undefined,
    createdFrom,
    createdTo,
    saleNumber: saleNumberQ,
    customerSearch: customerSearchQ,
    limit,
    offset,
  };

  const list = usePosSalesListQuery(shopId, listQuery);
  const items = list.data?.items ?? [];
  const total = list.data?.meta.total ?? 0;

  // ສະຫຼຸບ ຄືນ/ປ່ຽນ ຕໍ່ບິນ — ສະເພາະ tab ຄືນ/ປ່ຽນ
  const summariesQ = usePosReturnSummariesQuery(
    shopId,
    listQuery.returnType,
    items.map((i) => i.id),
  );
  const returnSummaries = summariesQ.data?.summaries;
  const pageEnd = Math.min(offset + limit, total);
  const hasPrev = offset > 0;
  const hasNext = offset + limit < total;

  const [detailId, setDetailId] = useState<string | null>(null);
  // ຄืน/ป่ຽนສินค้า — ต้องประกาศก่อน returnDetailQ (กัน TDZ)
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnMode, setReturnMode] = useState<"return" | "exchange">("return");
  const [returnSaleId, setReturnSaleId] = useState<string | null>(null);

  const detailQ = usePosSaleDetailQuery(
    shopId,
    detailId ?? undefined,
    !!detailId,
  );

  // detail ສຳລັບ return/exchange modal (ເປີດຈາກ list ໂດຍບໍ່ຕ້ອງເປີດ detail modal)
  const returnDetailQ = usePosSaleDetailQuery(
    shopId,
    returnSaleId ?? undefined,
    !!returnSaleId,
  );

  const voidMut = usePosVoidSaleMutation(shopId);
  const refundMut = usePosRefundSaleMutation(shopId);
  const abandonMut = usePosAbandonHoldMutation(shopId);
  const confirmMut = usePosConfirmHoldMutation(shopId);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSaleId, setConfirmSaleId] = useState<string | null>(null);
  const [confirmSaleNet, setConfirmSaleNet] = useState<number | null>(null);
  const [confirmTaxPercent, setConfirmTaxPercent] = useState(0);
  const [confirmCurrency, setConfirmCurrency] = useState("LAK");
  const [payMethod, setPayMethod] = useState<"cash" | "transfer" | "wallet">(
    "cash",
  );
  const [payRef, setPayRef] = useState("");
  const [invBuyerName, setInvBuyerName] = useState("");
  const [invBuyerTaxId, setInvBuyerTaxId] = useState("");
  const [invBuyerAddress, setInvBuyerAddress] = useState("");

  const [settleOpen, setSettleOpen] = useState(false);
  const [settleSaleId, setSettleSaleId] = useState<string | null>(null);
  const [confirmSaleBranchId, setConfirmSaleBranchId] = useState<string | null>(
    null,
  );

  const canVoid = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.pos.void,
    verifiedLevel,
  );
  const canRefund = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.pos.refund,
    verifiedLevel,
  );
  const canReturn = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.pos.return,
    verifiedLevel,
  );
  const canSell = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.pos.sell,
    verifiedLevel,
  );

  const setStatusFilter = (v: string) => {
    if (!shopId) return;
    void nav({
      to: ROUTE,
      params: { shopId },
      search: {
        ...posSalesNavSearch(search, { limit, offset: 0 }),
        status: v === FILTER_ALL ? undefined : v,
      },
    });
  };

  const clearListFilters = () => {
    if (!shopId) return;
    const cleared: PosSalesRouteSearch = {
      offset: 0,
      limit,
      status: undefined,
      createdFrom: undefined,
      createdTo: undefined,
      saleNumber: undefined,
      customerUserId: undefined,
      customerSearch: undefined,
    };
    void nav({
      to: ROUTE,
      params: { shopId },
      search: cleared,
    });
  };

  const detail = detailQ.data;

  const runVoid = async (
    id: string,
    saleNumber: string,
    saleBranchId: string,
  ) => {
    const ok = await confirm({
      title: "ຍົກເລີກບິນ",
      description: `ຢືນຢັນຍົກເລີກບິນ #${saleNumber} ຫຼື ບໍ່? ບິນຈະຖືກຍົກເລີກ ແລະ ສະຕ໋ອກຈະຖືກຄືນ.`,
      actionText: "ຍົກເລີກບິນ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      await voidMut.mutateAsync({ saleId: id, branchId: saleBranchId });
      toast.success("ຍົກເລີກບິນແລ້ວ (ຄືນສະຕ໋ອກ)");
      setDetailId(null);
      await list.refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ລົ້ມເຫຼວ");
    }
  };

  const runRefund = async (
    id: string,
    saleNumber: string,
    saleBranchId: string,
  ) => {
    const ok = await confirm({
      title: "ຄືນເງິນ",
      description: `ຢືນຢັນຄືນເງິນສຳລັບບິນ #${saleNumber} ຫຼື ບໍ່? ສະຖານະບິນຈະປ່ຽນ ແລະ ສະຕ໋ອກຈະຖືກຄືນ.`,
      actionText: "ຄືນເງິນ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      await refundMut.mutateAsync({ saleId: id, branchId: saleBranchId });
      toast.success("ຄືນເງິນແລ້ວ (ຄືນສະຕ໋ອກ)");
      setDetailId(null);
      await list.refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ລົ້ມເຫຼວ");
    }
  };

  const runAbandon = async (id: string, saleBranchId: string) => {
    try {
      await abandonMut.mutateAsync({ saleId: id, branchId: saleBranchId });
      toast.success("ຍົກເລີກພັກບິນແລ້ວ");
      setDetailId(null);
      await list.refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ລົ້ມເຫຼວ");
    }
  };

  const confirmPayBreakdown = useMemo(() => {
    if (confirmSaleNet == null || confirmSaleNet <= 0) {
      return { grandTotal: 0, taxTotal: 0 };
    }
    return applyExclusivePosTax(confirmSaleNet, confirmTaxPercent);
  }, [confirmSaleNet, confirmTaxPercent]);

  const openConfirm = (
    id: string,
    saleNetBeforeTax: number,
    currency: string,
    saleBranchId: string,
  ) => {
    setConfirmSaleId(id);
    setConfirmSaleBranchId(saleBranchId);
    setConfirmSaleNet(saleNetBeforeTax);
    setConfirmCurrency(currency);
    setConfirmTaxPercent(
      round2(
        Math.min(Math.max(0, Number(currentShop?.defaultTaxPercent) || 0), 100),
      ),
    );
    setPayMethod("cash");
    setPayRef("");
    setInvBuyerName("");
    setInvBuyerTaxId("");
    setInvBuyerAddress("");
    setConfirmOpen(true);
  };

  const submitConfirm = async () => {
    const effBranch = confirmSaleBranchId ?? branchId;
    if (!confirmSaleId || !shopId || !effBranch) return;
    if (confirmSaleNet == null || confirmSaleNet <= 0) {
      toast.error("ບໍ່ມີຍອດບິນ");
      return;
    }
    if (payMethod === "transfer" && !payRef.trim()) {
      toast.error("ໂອນຕ້ອງໃສ່ເລກອ້າງອີງ");
      return;
    }
    const idem =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}`;
    try {
      const inv =
        invBuyerName.trim() || invBuyerTaxId.trim() || invBuyerAddress.trim()
          ? {
              buyerName: invBuyerName.trim() || undefined,
              buyerTaxId: invBuyerTaxId.trim() || undefined,
              buyerAddress: invBuyerAddress.trim() || undefined,
            }
          : undefined;
      await confirmMut.mutateAsync({
        saleId: confirmSaleId,
        branchId: effBranch,
        body: {
          payments: [
            {
              method: payMethod,
              amount: round2(confirmPayBreakdown.grandTotal),
              reference: payMethod === "transfer" ? payRef.trim() : undefined,
            },
          ],
          ...(inv ? { invoice: inv } : {}),
          taxPercent: round2(Math.min(Math.max(0, confirmTaxPercent), 100)),
        },
        idempotencyKey: idem,
      });
      toast.success("ຊຳລະຈາກບິນຄ້າງສຳເລັດ");
      setConfirmOpen(false);
      setConfirmSaleId(null);
      setConfirmSaleNet(null);
      setDetailId(null);
      await list.refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ຊຳລະລົ້ມເຫຼວ");
    }
  };

  const branchTitle = currentShop?.branchName
    ? formatPosBranchDisplayLabel(currentShop.branchName, currentShop.shopName)
    : null;

  const activeFilterCount = [
    statusKey !== FILTER_ALL,
    createdFrom,
    createdTo,
    saleNumberQ,
    customerSearchQ,
  ].filter(Boolean).length;

  const navSearchPatch = (patch: Partial<PosSalesRouteSearch>) => {
    if (!shopId) return;
    void nav({
      to: ROUTE,
      params: { shopId },
      search: {
        ...posSalesNavSearch(search, { limit, offset: 0 }),
        ...patch,
      },
    });
  };

  return (
    <RequireMerchantPermission permission={MerchantPermissions.pos.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.posSales(shopId)} />
      </Header>
      <Main fluid className="max-w-none px-4 py-5 lg:px-6">
        <ShopPosSalesHistoryToolbar
          shopId={shopId}
          branchTitle={branchTitle}
          total={total}
          offset={offset}
          pageEnd={pageEnd}
          activeFilterCount={activeFilterCount}
          isRefreshing={list.isFetching && !list.isLoading}
          onRefresh={() => void list.refetch()}
        />

        <ShopPosSalesHistoryFilters
          disabled={!shopId}
          statusKey={statusKey}
          createdFrom={createdFrom}
          createdTo={createdTo}
          saleNumberQ={saleNumberQ}
          customerSearchQ={customerSearchQ}
          activeFilterCount={activeFilterCount}
          onStatusChange={setStatusFilter}
          onCreatedFromChange={(v) => navSearchPatch({ createdFrom: v })}
          onCreatedToChange={(v) => navSearchPatch({ createdTo: v })}
          onSaleNumberChange={(v) => navSearchPatch({ saleNumber: v })}
          onCustomerSearchChange={(v) => navSearchPatch({ customerSearch: v })}
          onClear={clearListFilters}
        />

        <ShopPosSalesHistoryList
          shopId={shopId}
          items={items}
          returnSummaries={returnSummaries}
          loading={list.isLoading}
          total={total}
          offset={offset}
          limit={limit}
          pageEnd={pageEnd}
          hasPrev={hasPrev}
          hasNext={hasNext}
          onSelect={setDetailId}
          onPrevPage={() => {
            if (!shopId) return;
            void nav({
              to: ROUTE,
              params: { shopId },
              search: posSalesNavSearch(search, {
                limit,
                offset: Math.max(0, offset - limit),
              }),
            });
          }}
          onNextPage={() => {
            if (!shopId) return;
            void nav({
              to: ROUTE,
              params: { shopId },
              search: posSalesNavSearch(search, {
                limit,
                offset: offset + limit,
              }),
            });
          }}
          canReturn={canReturn}
          onReturn={(id) => {
            setReturnSaleId(id);
            setReturnMode("return");
            setReturnOpen(true);
          }}
          onExchange={(id) => {
            setReturnSaleId(id);
            setReturnMode("exchange");
            setReturnOpen(true);
          }}
        />

        <SaleDetailModal
          open={!!detailId}
          onOpenChange={(o) => {
            if (!o) setDetailId(null);
          }}
          shopId={shopId}
          loading={detailQ.isLoading}
          detail={detail ?? null}
          receiptContext={
            currentShop
              ? {
                  shopName: currentShop.shopName,
                  branchName: detail?.branchName ?? currentShop.branchName,
                }
              : undefined
          }
          canVoid={canVoid}
          canRefund={canRefund}
          canSell={canSell}
          onVoid={runVoid}
          onRefund={runRefund}
          onAbandon={runAbandon}
          onConfirmPay={openConfirm}
          onSettlePartial={(id) => {
            setSettleSaleId(id);
            setSettleOpen(true);
          }}
          voidPending={voidMut.isPending}
          refundPending={refundMut.isPending}
          abandonPending={abandonMut.isPending}
        />

        <ShopPosReturnModal
          open={returnOpen}
          onOpenChange={(o) => {
            setReturnOpen(o);
            if (!o) setReturnSaleId(null);
          }}
          shopId={shopId}
          branchId={branchId}
          detail={returnDetailQ.data ?? null}
          mode={returnMode}
          onDone={() => setDetailId(null)}
        />

        <ShopPosSettlePartialModal
          shopId={shopId}
          branchId={branchId}
          saleId={settleSaleId}
          open={settleOpen}
          onOpenChange={(o) => {
            setSettleOpen(o);
            if (!o) setSettleSaleId(null);
          }}
        />

        <Modal
          open={confirmOpen}
          onOpenChange={(o) => {
            setConfirmOpen(o);
            if (!o) setConfirmSaleBranchId(null);
          }}
          title="ຊຳລະບິນຄ້າງ"
          description="ເລືອກວິທີຊຳລະ — ຈະຕັດສະຕ໋ອກຕາມບິນຄ້າງ"
          size="md"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              {(
                [
                  { k: "cash" as const, label: "ເງິນສົດ" },
                  { k: "transfer" as const, label: "ໂອນ" },
                  { k: "wallet" as const, label: "Wallet" },
                ] as const
              ).map(({ k, label }) => (
                <Button
                  key={k}
                  type="button"
                  size="sm"
                  variant={payMethod === k ? "default" : "outline"}
                  className="h-10 font-medium text-xs"
                  onClick={() => setPayMethod(k)}
                >
                  {label}
                </Button>
              ))}
            </div>
            {payMethod === "transfer" ? (
              <Input
                value={payRef}
                onChange={(e) => setPayRef(e.target.value)}
                placeholder="ເລກອ້າງອີງ"
                className="font-mono text-sm"
              />
            ) : null}
            {confirmSaleNet != null && confirmSaleNet > 0 ? (
              <div className="space-y-2 rounded-lg border border-border bg-card/80 p-3 text-sm shadow-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">ຍອດກ່ອນVAT</span>
                  <span className="tabular-nums">
                    {formatPrice(confirmSaleNet, confirmCurrency)}
                  </span>
                </div>
                <div>
                  <label
                    htmlFor="pos-confirm-hold-tax-pct"
                    className="mb-1 block font-medium text-card-foreground text-xs"
                  >
                    VAT (%)
                  </label>
                  <Input
                    id="pos-confirm-hold-tax-pct"
                    type="number"
                    inputMode="decimal"
                    min={0}
                    max={100}
                    step={0.01}
                    autoComplete="off"
                    value={
                      Number.isFinite(confirmTaxPercent) ? confirmTaxPercent : 0
                    }
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      setConfirmTaxPercent(
                        Number.isFinite(n) ? Math.min(Math.max(0, n), 100) : 0,
                      );
                    }}
                    className="h-9 max-w-[10rem] font-mono text-sm"
                    placeholder="0"
                  />
                </div>
                {confirmTaxPercent > 0.0001 ||
                confirmPayBreakdown.taxTotal > 0.009 ? (
                  <div className="flex justify-between gap-3">
                    <span className="text-muted-foreground">
                      VAT ({formatPosTaxPercentLabel(confirmTaxPercent)}%)
                    </span>
                    <span className="tabular-nums">
                      {formatPrice(
                        confirmPayBreakdown.taxTotal,
                        confirmCurrency,
                      )}
                    </span>
                  </div>
                ) : null}
                <div className="flex justify-between gap-3 border-border border-t pt-2 font-heading font-semibold text-foreground">
                  <span>ຕ້ອງຊຳລະ</span>
                  <span className="tabular-nums">
                    {formatPrice(
                      confirmPayBreakdown.grandTotal,
                      confirmCurrency,
                    )}
                  </span>
                </div>
              </div>
            ) : null}
            <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
              <p className="font-medium text-card-foreground text-xs">
                ໃບກຳກັບພາສີ (ທາງເລືອກ)
              </p>
              <Input
                value={invBuyerName}
                onChange={(e) => setInvBuyerName(e.target.value)}
                placeholder="ຊື່ຜູ້ຊື້ / ນິຕິບຸກຄົນ"
                className="h-9 text-sm"
              />
              <Input
                value={invBuyerTaxId}
                onChange={(e) => setInvBuyerTaxId(e.target.value)}
                placeholder="ເລກປະຈຳຕົວຜູ້ເສຍVATຜູ້ຊື້"
                className="h-9 font-mono text-sm"
              />
              <textarea
                value={invBuyerAddress}
                onChange={(e) => setInvBuyerAddress(e.target.value)}
                placeholder="ທີ່ຢູ່ຜູ້ຊື້"
                rows={2}
                className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmOpen(false)}
                disabled={confirmMut.isPending}
              >
                ຍົກເລີກ
              </Button>
              <Button
                type="button"
                className="font-semibold"
                onClick={() => void submitConfirm()}
                disabled={confirmMut.isPending}
              >
                {confirmMut.isPending ? <Loader /> : null}
                ຢືນຢັນ
              </Button>
            </div>
          </div>
        </Modal>
      </Main>
    </RequireMerchantPermission>
  );
}

function SaleDetailModal({
  open,
  onOpenChange,
  shopId,
  loading,
  detail,
  receiptContext,
  canVoid,
  canRefund,
  canSell,
  onVoid,
  onRefund,
  onAbandon,
  onConfirmPay,
  onSettlePartial,
  voidPending,
  refundPending,
  abandonPending,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  shopId: string | undefined;
  loading: boolean;
  detail: PosSaleDetailResponse | null;
  receiptContext?: { shopName: string; branchName: string | null };
  canVoid: boolean;
  canRefund: boolean;
  canSell: boolean;
  onVoid: (id: string, saleNumber: string, saleBranchId: string) => void;
  onRefund: (id: string, saleNumber: string, saleBranchId: string) => void;
  onAbandon: (id: string, saleBranchId: string) => void;
  onConfirmPay: (
    id: string,
    saleNetBeforeTax: number,
    currency: string,
    saleBranchId: string,
  ) => void;
  onSettlePartial: (id: string) => void;
  voidPending: boolean;
  refundPending: boolean;
  abandonPending: boolean;
}) {
  const [detailTab, setDetailTab] = useState<"bill" | "activity">("bill");

  // ສະຫຼຸບ ປ່ຽນ ຂອງບິນນີ້ (ຄืน [ເກົ່າ] → ໃໝ່ + ສ່ວນຕ່າງ)
  const exchangeSummaryQ = usePosReturnSummariesQuery(
    shopId,
    "exchange",
    open && detail?.id ? [detail.id] : [],
  );
  const exchangeSummary =
    detail?.id != null
      ? exchangeSummaryQ.data?.summaries?.[detail.id]
      : undefined;

  useEffect(() => {
    if (!open) setDetailTab("bill");
  }, [open]);

  useEffect(() => {
    setDetailTab("bill");
  }, [detail?.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton
        className={cn(
          "flex max-h-[min(92dvh,calc(100dvh-1rem))] w-[calc(100vw-1.5rem)] max-w-[min(56rem,calc(100vw-1.5rem))] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl md:max-w-3xl lg:max-w-5xl",
        )}
      >
        <DialogHeader className="shrink-0 space-y-1 border-border border-b bg-muted/20 px-5 py-4 text-start sm:px-6">
          <DialogTitle className="font-heading text-lg leading-tight sm:text-xl">
            {detail ? `ບິນ #${detail.saleNumber}` : "ລາຍລະອຽດບິນ"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            ລາຍລະອຽດບິນຂາຍ POS ສາຂານີ້
          </DialogDescription>
        </DialogHeader>
        {detail && !loading ? (
          <div
            className="flex shrink-0 gap-1 border-border border-b bg-muted/10 px-5 py-2 sm:px-6"
            role="tablist"
            aria-label="ລາຍລະອຽດບິນ"
          >
            <Button
              type="button"
              role="tab"
              aria-selected={detailTab === "bill"}
              variant={detailTab === "bill" ? "default" : "ghost"}
              size="sm"
              className="gap-1.5"
              onClick={() => setDetailTab("bill")}
            >
              <Package className="size-4 shrink-0" aria-hidden />
              ຂໍ້ມູນບິນ
            </Button>
            <Button
              type="button"
              role="tab"
              aria-selected={detailTab === "activity"}
              variant={detailTab === "activity" ? "default" : "ghost"}
              size="sm"
              className="gap-1.5"
              onClick={() => setDetailTab("activity")}
            >
              <History className="size-4 shrink-0" aria-hidden />
              ປະຫວັດ
            </Button>
          </div>
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4 [-webkit-overflow-scrolling:touch] sm:px-6">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader />
            </div>
          ) : !detail ? (
            <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
          ) : detailTab === "activity" && shopId ? (
            <PosSaleActivitySection shopId={shopId} saleId={detail.id} />
          ) : detailTab === "activity" ? (
            <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
          ) : (
            <SaleDetailBody
              detail={detail}
              exchangeSummary={exchangeSummary}
              receiptContext={receiptContext}
              canVoid={canVoid}
              canRefund={canRefund}
              canSell={canSell}
              onVoid={onVoid}
              onRefund={onRefund}
              onAbandon={onAbandon}
              onConfirmPay={onConfirmPay}
              onSettlePartial={onSettlePartial}
              voidPending={voidPending}
              refundPending={refundPending}
              abandonPending={abandonPending}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SaleDetailBody({
  detail,
  exchangeSummary,
  receiptContext,
  canVoid,
  canRefund,
  canSell,
  onVoid,
  onRefund,
  onAbandon,
  onConfirmPay,
  onSettlePartial,
  voidPending,
  refundPending,
  abandonPending,
}: {
  detail: PosSaleDetailResponse;
  exchangeSummary?: PosReturnSummary;
  receiptContext?: { shopName: string; branchName: string | null };
  canVoid: boolean;
  canRefund: boolean;
  canSell: boolean;
  onVoid: (id: string, saleNumber: string, saleBranchId: string) => void;
  onRefund: (id: string, saleNumber: string, saleBranchId: string) => void;
  onAbandon: (id: string, saleBranchId: string) => void;
  onConfirmPay: (
    id: string,
    saleNetBeforeTax: number,
    currency: string,
    saleBranchId: string,
  ) => void;
  onSettlePartial: (id: string) => void;
  voidPending: boolean;
  refundPending: boolean;
  abandonPending: boolean;
}) {
  const paidSum = sumPaymentsPaid(detail);
  const outstanding = round2(Math.max(0, detail.total - paidSum));
  const hasDiscount = detail.discountTotal > 0.009;
  const hasTax = detail.taxTotal > 0.009 || (detail.taxPercent ?? 0) > 0.0001;
  const netBeforeTaxForDisplay = roundMoneyPos(
    Math.max(0, detail.subtotal - detail.discountTotal),
  );

  const effectiveTaxPctForDisplay = effectivePosTaxPercentDisplay(
    netBeforeTaxForDisplay,
    detail.taxTotal,
    detail.taxPercent ?? 0,
  );
  const taxRowPercentLabel = formatPosTaxPercentLabel(
    effectiveTaxPctForDisplay,
  );

  const inv = detail.invoice;
  const showTaxMeta =
    inv &&
    (inv.buyerName || inv.buyerTaxId || inv.buyerAddress || inv.sellerTaxId);

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-border bg-card px-4 py-3 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={saleStatusBadgeVariant(detail.status)}>
              {statusLabel(detail.status)}
            </Badge>
            <span className="rounded-md bg-muted px-2 py-0.5 font-mono text-[11px] text-muted-foreground">
              {detail.currency}
            </span>
            {detail.branchName != null && detail.branchName.trim() !== "" ? (
              <span className="text-muted-foreground text-xs">
                ສາຂາ{" "}
                <span className="font-medium text-foreground">
                  {detail.branchName.trim()}
                </span>
              </span>
            ) : null}
          </div>
          <div className="text-end">
            <p className="text-muted-foreground text-xs leading-tight">
              {formatDateTimeLocal(detail.createdAt)}
            </p>
            {detail.status === "draft" && detail.holdExpiresAt ? (
              <p className="mt-1 text-secondary text-xs">
                ໝົດອາຍຸພັກ: {formatDateTimeLocal(detail.holdExpiresAt)}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-start lg:gap-8">
        <div className="min-w-0 space-y-5 lg:col-span-7">
          <section className="space-y-2">
            <h3 className="flex items-center gap-2 font-heading font-semibold text-card-foreground text-xs uppercase tracking-wide">
              <Package
                className="size-4 shrink-0 text-[hsl(210_90%_48%)]"
                aria-hidden
              />
              ລາຍການສິນຄ້າ
            </h3>
            <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
              {detail.lines.length === 0 ? (
                <p className="px-4 py-8 text-center text-muted-foreground text-sm">
                  ບໍ່ມີລາຍການ
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[280px] text-sm">
                    <thead>
                      <tr className="border-border border-b bg-muted/60 text-left text-[11px] text-muted-foreground uppercase tracking-wide">
                        <th className="px-4 py-2.5 font-medium">ສິນຄ້າ</th>
                        <th className="px-4 py-2.5 text-end font-medium">
                          ຈຳນວນ
                        </th>
                        <th className="px-4 py-2.5 text-end font-medium">
                          ລວມ
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {detail.lines.map((l) => (
                        <tr
                          key={l.id}
                          className="transition-colors hover:bg-muted/40"
                        >
                          <td className="px-4 py-3 align-top">
                            <p className="font-medium text-card-foreground leading-snug">
                              {l.productName}
                            </p>
                            <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                              {l.shopSku}
                            </p>
                            {l.warranty ? (
                              <div className="mt-2 space-y-0.5 border-border/60 border-t pt-2">
                                <p className="flex items-center gap-1 text-[11px] text-secondary">
                                  <ShieldCheck
                                    className="size-3.5 shrink-0"
                                    aria-hidden
                                  />
                                  <span className="font-medium">ປະກັນ</span>
                                </p>
                                <p className="text-[11px] text-muted-foreground leading-snug">
                                  ຫມົດ{" "}
                                  {formatDateTimeLocal(
                                    l.warranty.coverageEndAt,
                                  )}
                                  {l.warranty.status === "voided"
                                    ? " · ຍົກເລີກບິນ"
                                    : null}
                                  {l.warranty.status === "expired"
                                    ? " · ໝົດອາຍຸ"
                                    : null}
                                </p>
                                {l.warranty.termsNoteSnapshot ? (
                                  <p className="text-[11px] text-muted-foreground leading-snug">
                                    {l.warranty.termsNoteSnapshot}
                                  </p>
                                ) : null}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-4 py-3 text-end align-top text-card-foreground tabular-nums">
                            {l.qty}
                          </td>
                          <td className="px-4 py-3 text-end align-top font-medium text-card-foreground tabular-nums">
                            {formatPrice(l.lineTotal, detail.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>

          {exchangeSummary ? (
            <section className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 shadow-sm">
              <h3 className="mb-2 flex items-center gap-2 font-heading font-semibold text-primary text-xs uppercase tracking-wide">
                <RefreshCw className="size-4 shrink-0" aria-hidden />
                ປ່ຽນສິນຄ້າ
              </h3>
              <div className="space-y-1 text-sm">
                <p>
                  <span className="font-medium text-red-600">ຄືນ:</span>{" "}
                  {exchangeSummary.oldItems.length > 0
                    ? exchangeSummary.oldItems
                        .map((i) => `${i.productName} x${i.qty}`)
                        .join(", ")
                    : "—"}
                </p>
                <p>
                  <span className="font-medium text-orange-600">ໃໝ່:</span>{" "}
                  {exchangeSummary.newItems.length > 0
                    ? exchangeSummary.newItems
                        .map((i) => `${i.productName} x${i.qty}`)
                        .join(", ")
                    : "—"}
                </p>
                <p className="text-muted-foreground text-xs">
                  ເງິນຄືນ (ເກົ່າ){" "}
                  {formatPrice(exchangeSummary.refundTotal, detail.currency)}
                </p>
              </div>
            </section>
          ) : null}

          {showTaxMeta ? (
            <div className="rounded-xl border border-border bg-muted/25 px-4 py-3 text-sm shadow-sm">
              <p className="mb-2 font-heading font-semibold text-[11px] text-muted-foreground uppercase tracking-wide">
                ຂໍ້ມູນ snapshot ໃບກຳກັບ
              </p>
              <dl className="grid gap-2 sm:grid-cols-2">
                {inv.sellerTaxId ? (
                  <div>
                    <dt className="text-muted-foreground text-xs">
                      ຜູ້ຂາຍ — VAT
                    </dt>
                    <dd className="font-mono text-card-foreground text-xs">
                      {inv.sellerTaxId}
                    </dd>
                  </div>
                ) : null}
                {inv.buyerName ? (
                  <div>
                    <dt className="text-muted-foreground text-xs">ຜູ້ຊື້</dt>
                    <dd className="text-card-foreground">{inv.buyerName}</dd>
                  </div>
                ) : null}
                {inv.buyerTaxId ? (
                  <div>
                    <dt className="text-muted-foreground text-xs">VATຜູ້ຊື້</dt>
                    <dd className="font-mono text-card-foreground text-xs">
                      {inv.buyerTaxId}
                    </dd>
                  </div>
                ) : null}
                {inv.buyerAddress ? (
                  <div className="sm:col-span-2">
                    <dt className="text-muted-foreground text-xs">ທີ່ຢູ່ຜູ້ຊື້</dt>
                    <dd className="whitespace-pre-wrap text-card-foreground text-xs">
                      {inv.buyerAddress}
                    </dd>
                  </div>
                ) : null}
              </dl>
            </div>
          ) : null}
        </div>

        <aside
          className={cn(
            "min-w-0 space-y-5 lg:col-span-5",
            "lg:sticky lg:top-0 lg:self-start lg:border-border lg:border-l lg:pl-8",
          )}
        >
          <div
            className="rounded-xl border border-border px-4 py-4 shadow-sm"
            style={{
              backgroundImage:
                "linear-gradient(135deg, color-mix(in oklab, var(--foreground) 5%, transparent) 0%, transparent 55%)",
            }}
          >
            <p className="mb-3 font-heading font-semibold text-[11px] text-muted-foreground uppercase tracking-wide">
              ສະຫຼຸບເງິນ
            </p>
            {hasDiscount || hasTax ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="text-muted-foreground">ລວມກ່ອນຫຼຸດ</span>
                  <span className="tabular-nums">
                    {formatPrice(detail.subtotal, detail.currency)}
                  </span>
                </div>
                {hasDiscount ? (
                  <div className="flex justify-between gap-4">
                    <span className="text-muted-foreground">ສ່ວນຫຼຸດ</span>
                    <span className="text-destructive tabular-nums">
                      −{formatPrice(detail.discountTotal, detail.currency)}
                    </span>
                  </div>
                ) : null}
                {hasTax ? (
                  <div className="flex justify-between gap-4">
                    <span className="text-muted-foreground">
                      {taxRowPercentLabel
                        ? `VAT (${taxRowPercentLabel}%)`
                        : "VAT"}
                    </span>
                    <span className="tabular-nums">
                      {formatPrice(detail.taxTotal, detail.currency)}
                    </span>
                  </div>
                ) : null}
                <div className="flex justify-between gap-4 border-border border-t pt-3">
                  <span className="font-medium text-card-foreground">
                    ຍອດຕ້ອງຊຳລະ
                  </span>
                  <span className="font-bold font-heading text-card-foreground text-lg tabular-nums tracking-tight">
                    {formatPrice(detail.total, detail.currency)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex items-end justify-between gap-4">
                <span className="pb-0.5 text-muted-foreground text-sm">
                  ລວມທັງໝົດ
                </span>
                <span className="font-bold font-heading text-2xl text-card-foreground tabular-nums tracking-tight">
                  {formatPrice(detail.total, detail.currency)}
                </span>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card px-4 py-4 shadow-sm">
            <h3 className="mb-3 font-heading font-semibold text-[11px] text-muted-foreground uppercase tracking-wide">
              ການຊຳລະ
            </h3>
            {detail.status === "draft" ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-secondary/40 border-dashed bg-secondary/5 px-3 py-3">
                <div>
                  <p className="font-medium text-card-foreground text-sm">
                    ຄ້າງຊຳລະ
                  </p>
                  <p className="mt-0.5 text-muted-foreground text-xs">
                    ຍັງບໍ່ມີການຊຳລະ
                  </p>
                </div>
                <span className="font-bold font-heading text-lg text-secondary tabular-nums">
                  {formatPrice(detail.total, detail.currency)}
                </span>
              </div>
            ) : null}
            {detail.payments.length > 0 ? (
              <ul className="space-y-3">
                {detail.payments.map((p) => (
                  <li
                    key={p.id}
                    className="rounded-lg border border-border/80 bg-muted/25 px-3 py-2.5"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2 font-medium text-card-foreground text-sm">
                        <PaymentMethodIcon method={p.method} />
                        {paymentMethodLabel(p.method)}
                      </span>
                      <span className="font-semibold text-card-foreground tabular-nums">
                        {formatPrice(p.amount, p.currency ?? detail.currency)}
                        {p.currency != null &&
                        p.currency !== detail.currency &&
                        p.invoiceEquivalentAmount != null &&
                        Number.isFinite(p.invoiceEquivalentAmount) ? (
                          <span className="ms-1 font-normal text-muted-foreground text-xs">
                            (≈{" "}
                            {formatPrice(
                              p.invoiceEquivalentAmount,
                              detail.currency,
                            )}
                            )
                          </span>
                        ) : null}
                      </span>
                    </div>
                    {p.method === "transfer" && p.reference?.trim() ? (
                      <p className="mt-1.5 border-border border-t border-dashed pt-1.5 font-mono text-[11px] text-muted-foreground">
                        ອ້າງອີງ: {p.reference.trim()}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : detail.status !== "draft" ? (
              <p className="rounded-lg bg-muted/40 px-3 py-2 text-center text-muted-foreground text-xs">
                ບໍ່ມີລາຍການຊຳລະ
              </p>
            ) : null}
            {detail.status !== "draft" && detail.payments.length > 0 ? (
              <div className="mt-4 space-y-2 border-border border-t pt-3">
                <div className="flex justify-between gap-3 text-sm">
                  <span className="text-muted-foreground">ລວມຊຳລະແລ້ວ</span>
                  <span className="font-semibold text-card-foreground tabular-nums">
                    {formatPrice(paidSum, detail.currency)}
                  </span>
                </div>
                {outstanding > 0.009 ? (
                  <div className="flex justify-between gap-3 rounded-md bg-secondary/10 px-2 py-1.5 text-sm">
                    <span className="font-medium text-card-foreground">
                      ຄ້າງຊຳລະ
                    </span>
                    <span className="font-bold font-heading text-secondary tabular-nums">
                      {formatPrice(outstanding, detail.currency)}
                    </span>
                  </div>
                ) : (
                  <p className="text-end text-[11px] text-muted-foreground">
                    ຊຳລະຄົບຕາມຍອດບິນ
                  </p>
                )}
              </div>
            ) : null}
          </div>

          <div className="space-y-3 print:hidden">
            <p className="font-heading font-semibold text-[11px] text-muted-foreground uppercase tracking-wide">
              ພິມ ແລະ ຈັດການ
            </p>
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-full shrink-0 text-muted-foreground text-xs lg:w-auto lg:min-w-[5.5rem]">
                  ໃບຮັບເງິນ
                </span>
                <div className="flex flex-1 flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 gap-1.5 font-medium"
                    title="ໃບຮັບເງິນ 80 mm"
                    onClick={() =>
                      printPosSaleReceipt(detail, receiptContext, {
                        paper: "thermal80",
                        template: "simple",
                      })
                    }
                  >
                    <Printer className="size-4 shrink-0" aria-hidden />
                    80&nbsp;mm
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 gap-1.5 font-medium"
                    title="ໃບຮັບເງິນ A4"
                    onClick={() =>
                      printPosSaleReceipt(detail, receiptContext, {
                        paper: "a4",
                        template: "simple",
                      })
                    }
                  >
                    <Printer className="size-4 shrink-0" aria-hidden />
                    A4
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-full shrink-0 text-muted-foreground text-xs lg:w-auto lg:min-w-[5.5rem]">
                  ໃບກຳກັບ
                </span>
                <div className="flex flex-1 flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 gap-1.5 font-medium"
                    title="ໃບກຳກັບ 80 mm"
                    onClick={() =>
                      printPosSaleReceipt(detail, receiptContext, {
                        paper: "thermal80",
                        template: "tax",
                      })
                    }
                  >
                    <Printer className="size-4 shrink-0" aria-hidden />
                    80&nbsp;mm
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 gap-1.5 font-medium"
                    title="ໃບກຳກັບ A4"
                    onClick={() =>
                      printPosSaleReceipt(detail, receiptContext, {
                        paper: "a4",
                        template: "tax",
                      })
                    }
                  >
                    <Printer className="size-4 shrink-0" aria-hidden />
                    A4
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 border-border border-t pt-3 sm:flex-row sm:flex-wrap sm:justify-end">
              {detail.status === "draft" && canSell ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={() => onAbandon(detail.id, detail.branchId)}
                    disabled={abandonPending}
                  >
                    ຍົກເລີກພັກ
                  </Button>
                  <Button
                    type="button"
                    className="w-full font-semibold sm:w-auto sm:min-w-28"
                    onClick={() =>
                      onConfirmPay(
                        detail.id,
                        detail.total,
                        detail.currency,
                        detail.branchId,
                      )
                    }
                  >
                    ຊຳລະຕໍ່
                  </Button>
                </>
              ) : null}
              {detail.status === "partial" && canSell && outstanding > 0.009 ? (
                <Button
                  type="button"
                  className="w-full font-semibold sm:w-auto sm:min-w-28"
                  onClick={() => onSettlePartial(detail.id)}
                >
                  ຊຳລະສ່ວນທີ່ເຫຼືອ
                </Button>
              ) : null}
              {detail.status === "paid" && canVoid ? (
                <Button
                  type="button"
                  variant="destructive"
                  className="w-full sm:w-auto"
                  onClick={() =>
                    onVoid(detail.id, detail.saleNumber, detail.branchId)
                  }
                  disabled={voidPending}
                >
                  ຍົກເລີກບິນ
                </Button>
              ) : null}
              {detail.status === "paid" && canRefund ? (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full sm:w-auto"
                  onClick={() =>
                    onRefund(detail.id, detail.saleNumber, detail.branchId)
                  }
                  disabled={refundPending}
                >
                  ຄືນເງິນ
                </Button>
              ) : null}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
